<script setup>
import { ref } from 'vue';
import { API } from '@/services/index';
import { UPDATE_ACCOUNT } from '@/services/account/mutations/account/index.js';
import { useAccountStore } from '@/stores/account.store.js';
import { useUserStore } from '@/stores/user.store.js';
import { useSettingsStore } from '@/stores/settings.store.js';
import { usePermissionsStore } from '@/stores/permissions.store.js';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import HeaderPartial from '@/pages/partials/settings/headerPartial.vue';
import SectionPartial from '@/pages/partials/settings/sectionPartial.vue';
import AccountTeamMembers from '@/pages/settings/account/components/accountTeamsTable.vue';
import { useFileUpload } from '@/composables/useFileUpload.js';
import imagePlaceholder from '@/assets/workspace-placeholder.png';

const queryClient = useQueryClient();
const permissions = usePermissionsStore();
const activeSettingsAccount = computed(() => useSettingsStore().activeAccount);
const isPermissionsLoading = computed(() => useUserStore().isPermissionsLoading);
const logoUploadInput = ref();
const logoFile = ref(null);
const isNameFormValid = ref(false);
const accountName = ref(null);
const isEditName = ref(false);
const isEditAccountType = ref(false);
const updateForm = reactive({
  fieldType: null,
  isLoading: false
});

const formRules = {
  accountName: (value) => !!value || 'Account name is required'
};

watchEffect(() => {
  if (activeSettingsAccount?.value) {
    accountName.value = activeSettingsAccount.value?.name;
  }
});

const accountMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/account', UPDATE_ACCOUNT, payload);
  },
  onSuccess: async (data) => {
    if (data.data.data.updateAccount) {
      // useAccountStore().setActiveAccount(data.data.data.updateAccount);
      useSettingsStore().setActiveAccount(data.data.data.updateAccount);
      await useAccountStore().getAccounts();

      switch (updateForm.fieldType) {
        case 'name':
          isEditName.value = false;
          break;
        case 'accountType':
          isEditAccountType.value = false;
          break;
      }
    }

    if (data.data.error) {
      // Helpers.showError(data.data.error.message);
      console.log(data.data.error);
    }
    updateForm.isLoading = false;
  },
  onError: (error) => {
    console.log('error', error);
    updateForm.fieldType = null;
    updateForm.isLoading = false;
  }
});

const updateNameHandler = async () => {
  updateForm.fieldType = 'name';
  updateForm.isLoading = true;

  const payload = {
    id: Number(activeSettingsAccount.value?.id),
    name: accountName.value,
    type_id: activeSettingsAccount.value?.type_id
  };

  await accountMutation.mutateAsync(payload);
};

const updateLogoHandler = async (payload) => {
  updateForm.fieldType = 'logo';
  updateForm.isLoading = true;

  if (payload?.removed) {
    await accountMutation.mutateAsync({
      id: Number(activeSettingsAccount.value?.id),
      remove_image: true
    });
  } else {
    const { uploadFile, uploadErrors, uploadSuccess, uploadedFileLocation } = useFileUpload();

    if (logoFile.value) {
      await uploadFile(logoFile.value);
      if (uploadSuccess.value && uploadedFileLocation.value) {
        await accountMutation.mutateAsync({
          id: Number(activeSettingsAccount.value?.id),
          image: uploadedFileLocation.value
        });
      }
    }
  }
};
</script>

<template>
  <div>
    <div class="tw-max-w-full lg:tw-max-w-[750px]">
      <HeaderPartial
        class="tw-capitalize"
        :style="!activeSettingsAccount ? 'color:#d8d8d8' : ''"
        :title="`${activeSettingsAccount ? activeSettingsAccount?.name : ''} Account Details`"
      />
      <SectionPartial
        label="Account Logo"
        :style="!activeSettingsAccount ? 'color:#d8d8d8' : ''"
        :isLoading="updateForm.fieldType === 'logo' && updateForm.isLoading"
        :subtitle="permissions.canUpdateAccount() ? 'Upload your account logo. This helps your teammates recognize you in PartnrUp.' : ''"
      >
        <div v-if="permissions.canUpdateAccount()" class="tw-mb-6 tw-text-[0.8rem] tw-font-medium tw-text-[#001ea4]">
          Suggestion - Please upload a circle image with the following dimensions: width and height 300px or higher, with a transparent background.
        </div>
        <div class="tw-flex tw-flex-col tw-items-center tw-justify-between tw-pb-5 lg:tw-flex-row">
          <div class="tw-flex tw-w-[125px] tw-items-center lg:tw-h-[147px] lg:tw-w-[300px]">
            <v-img
              contain
              v-if="activeSettingsAccount?.image"
              class="tw-mb-4 tw-h-full tw-w-full tw-rounded-md lg:tw-mb-0"
              :class="!activeSettingsAccount.image ? 'lg:tw-w-[300px]' : ''"
              :src="activeSettingsAccount?.image ?? imagePlaceholder"
              :alt="`${activeSettingsAccount?.name} account logo`"
            ></v-img>
          </div>
          <div
            class="tw-mt-8 tw-flex tw-w-full tw-space-x-2 lg:tw-mt-0 lg:tw-w-[16rem] lg:tw-items-center"
            :class="activeSettingsAccount?.image ? 'tw-justify-between' : 'tw-justify-end'"
            v-if="permissions.canUpdateAccount()"
          >
            <v-btn
              v-if="activeSettingsAccount?.image"
              :disabled="isPermissionsLoading || (updateForm.fieldType === 'logo' && updateForm.isLoading)"
              class="tw-capitalize"
              variant="tonal"
              size="small"
              height="40px"
              @click="updateLogoHandler({ removed: true })"
            >
              Remove Logo
            </v-btn>
            <v-btn
              class="tw-capitalize"
              :disabled="isPermissionsLoading || (updateForm.fieldType === 'logo' && updateForm.isLoading)"
              :isLoading="updateForm.fieldType === 'logo' && updateForm.isLoading"
              variant="tonal"
              color="primary-blue"
              size="small"
              height="40px"
              @click="logoUploadInput.click()"
            >
              {{ activeSettingsAccount?.image ? 'Change Logo' : 'Upload Logo' }}
            </v-btn>
          </div>
          <v-file-input
            v-model="logoFile"
            ref="logoUploadInput"
            accept="image/*"
            class="tw-hidden"
            @update:modelValue="updateLogoHandler"
          ></v-file-input>
        </div>
      </SectionPartial>
      <SectionPartial
        label="Account Name"
        :style="!activeSettingsAccount ? 'color:#d8d8d8' : ''"
        :isLoading="updateForm.fieldType === 'name' && updateForm.isLoading"
      >
        <template v-if="!isEditName">
          <div class="tw-flex tw-items-center tw-justify-between">
            <p class="tw-mb-0 tw-text-sm tw-text-[#0d1216]">
              {{ accountName }}
            </p>

            <v-btn
              v-if="permissions.canUpdateAccount()"
              :disabled="isPermissionsLoading"
              class="tw-rounded-sm tw-capitalize"
              @click="isEditName = true"
              variant="tonal"
              size="small"
              height="40px"
            >
              Edit
            </v-btn>
          </div>
        </template>
        <template v-else>
          <v-form ref="nameForm" v-model="isNameFormValid" @submit.prevent="updateNameHandler">
            <div class="tw-flex tw-flex-col tw-items-start tw-justify-between lg:tw-flex-row lg:tw-space-x-2">
              <div class="tw-flex tw-w-full tw-flex-col">
                <v-text-field
                  v-model="accountName"
                  variant="outlined"
                  :rules="[formRules.accountName]"
                  color="primary-blue"
                  label=""
                  placeholder="Account Name*"
                  hide-details="auto"
                  density="compact"
                  :focused="true"
                  :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
                ></v-text-field>
              </div>
              <div class="tw-mt-3 tw-flex tw-w-full tw-items-center tw-justify-between lg:tw-ml-16 lg:tw-mt-0 lg:tw-w-[9rem] lg:tw-space-x-3">
                <v-btn
                  class="tw-capitalize"
                  variant="tonal"
                  size="small"
                  height="40px"
                  @click="isEditName = false"
                  :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
                >
                  Cancel
                </v-btn>
                <v-btn
                  class="tw-capitalize"
                  variant="tonal"
                  size="small"
                  height="40px"
                  color="primary-blue"
                  type="submit"
                  :loading="updateForm.isLoading && updateForm.fieldType === 'name'"
                  :disabled="(updateForm.isLoading && updateForm.fieldType === 'name') || !isNameFormValid"
                >
                  Save
                </v-btn>
              </div>
            </div>
          </v-form>
        </template>
      </SectionPartial>
    </div>
    <v-progress-circular
      v-if="isPermissionsLoading"
      width="2"
      size="24"
      color="primary"
      indeterminate
      class="tw-m-auto tw-my-1"
      style="width: 2%"
    ></v-progress-circular>
    <template v-else>
      <div v-if="activeSettingsAccount && permissions.canUpdateAccount()" class="">
        <HeaderPartial :style="!activeSettingsAccount ? 'color:#d8d8d8' : ''" title="Team Members" />
        <SectionPartial :divider="false">
          <AccountTeamMembers />
        </SectionPartial>
      </div>
    </template>
  </div>
</template>

<style scoped></style>
